import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import colors from "core/colors";

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, "toString", {
            value: () => "",
            enumerable: false
        });

    const getDimensions = (size, sizes) => {
        if (
            size &&
            typeof size.width === "number" &&
            typeof size.height === "number"
        ) {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return "";
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule =
            fillColor && fillColorRule
                ? `${fillColorRule}{ fill: ${fillColor}; }`
                : "";

        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes
    };
};

const width = "190";
const height = "190";
const viewBox = "0 0 190 190";

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(
    width,
    height,
    css
);

const sizes = sanitizeSizes({
    sm: { width: 98, height: 98 },
    md: { width: 100, height: 100 },
    lg: { width: 190, height: 190 }
});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path
            fill={colors.midnight}
            fillRule="evenodd"
            d="M100 28.777c4.008-.91 7-4.494 7-8.777a9 9 0 10-18 0 9.004 9.004 0 006 8.488V54H51a3 3 0 00-3 3v33H19.777A9 9 0 1011 101a9.004 9.004 0 008.488-6H48v37a3 3 0 003 3h44v27.512A9.004 9.004 0 0089 171a9 9 0 009 9 9 9 0 009-9 9.003 9.003 0 00-7-8.777V135h39a3 3 0 003-3V95h28.512a9.004 9.004 0 008.488 6 9 9 0 009-9 9 9 0 00-9-9 9.003 9.003 0 00-8.777 7H142V57a3 3 0 00-3-3h-39V28.777zm.797 61.527L98.493 88l-4.053 4.052L90.467 88l-2.304 2.304 3.973 4.053-3.973 3.973 2.304 2.264 3.973-4.052 4.053 4.052 2.304-2.264-4.092-3.973 4.092-4.053zm-23.56 10.29a6.238 6.238 0 100-12.476 6.238 6.238 0 000 12.476zM105.367 88H118v12.634h-12.634V88zM53 130V59h84v71H53z"
            clipRule="evenodd"
            key="key-0"
        />
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: "&&& path, &&& use, &&& g",
    sizes,
    size: null
};

Image.propTypes /* remove-proptypes */ = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired
        })
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
    })
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    displayName: "Apps2"
});
